import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import type { AgentTask } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { useAgentTaskDuplexer } from './use-agent-task-duplexer';
import { queryKeys } from './query-client';
import { agentTasksAreEmpty, bucketOfTask, bucketTasks, type AgentTaskBucket } from './agent-task-buckets';

// Client data layer for the workspace "Agents at work" screen. One fetch to
// /api/workspace-tasks?workspaceId= returns the workspace's (and its child projects') raw v2 AgentTasks;
// the card renders off the AgentTask's `title` / `status` / `display`.
//
// Bucketing and the recency window come from lib/agent-task-buckets — the SAME source the Agent Tasks
// screen these cards link to uses, so a card's count is a true promise about what that tab contains.

export type WorkspaceTasksState = { status: 'loading' } | { status: 'ready'; tasks: AgentTask[] } | { status: 'error' };

/** The card row shows the three primary states; a failed task is surfaced by the Agent Tasks Failed tab. */
export type WorkspaceTaskBucket = Extract<AgentTaskBucket, 'waiting' | 'inProgress' | 'done'>;

export const bucketOf = bucketOfTask;

/**
 * A settled workspace with nothing at all in the window — the Agents-at-work Tasks section hides its
 * "All tasks" link and shows the empty state instead of zero-count cards (Figma), matching the native tab.
 *
 * Counts every bucket, INCLUDING failed: a workspace whose only tasks failed still has tasks, and calling
 * it empty here would contradict the Failed tab that "All tasks" opens.
 */
export const workspaceTasksAreEmpty = agentTasksAreEmpty;

/** The windowed per-bucket split the stat cards count from. */
export function bucketWorkspaceTasks(tasks: AgentTask[]): Record<AgentTaskBucket, AgentTask[]> {
  return bucketTasks(tasks);
}

export async function fetchWorkspaceTasks(workspaceId: string): Promise<AgentTask[]> {
  const res = await apiFetch(`${API_BASE}/api/workspace-tasks?workspaceId=${encodeURIComponent(workspaceId)}`, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const data = (await res.json()) as { tasks?: AgentTask[] };
  return data.tasks ?? [];
}

export function useWorkspaceTasks(workspaceId: string | undefined): WorkspaceTasksState {
  const query = useQuery({
    queryKey: queryKeys.workspaceTasks(workspaceId),
    queryFn: () => fetchWorkspaceTasks(workspaceId as string),
    enabled: !!workspaceId,
  });

  // AgentTask created/progressed/deleted by chat refreshes the list live.
  useAgentTaskDuplexer(() => {
    if (workspaceId) void query.refetch();
  });

  // Keep a prior good list on a transient refetch failure — only surface an error before the first success.
  if (query.data) return { status: 'ready', tasks: query.data };
  if (query.status === 'error') return { status: 'error' };
  return { status: 'loading' };
}
